import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialCommercialCartSelection, initialShoppingCart } from './shopping_cart.initial';
import { isPremiumMonthSelectionInvalid } from './shopping_cart.constants';
import { getSuggestedCalc } from './thunks';

export const shoppingCartSlice = createSlice({
    name: 'shopping_cart',
    initialState: initialShoppingCart,
    reducers: {
        setSuggestions: (state, action) => {
            state.suggested = action.payload;
        },
        toggleProductSelection: (
            state,
            action: PayloadAction<{
                productId: number;
                willBeSelected: boolean;
                productIdsToClear: number[];
                initialQuantity?: number;
                initialPrice?: number;
            }>
        ) => {
            const { productId, willBeSelected, productIdsToClear, initialQuantity = 1, initialPrice = 1 } = action.payload;
            const sel = state.suggested.selection;
            const nextSelected = { ...sel.selectedByProductId, [productId]: willBeSelected };
            productIdsToClear.forEach((id) => delete nextSelected[id]);
            state.suggested.selection.selectedByProductId = nextSelected;

            const nextQ = willBeSelected ? { ...sel.quantityByProductId, [productId]: initialQuantity } : { ...sel.quantityByProductId };
            if (!willBeSelected) delete nextQ[productId];
            productIdsToClear.forEach((id) => delete nextQ[id]);
            state.suggested.selection.quantityByProductId = nextQ;

            const nextP = willBeSelected ? { ...sel.priceByProductId, [productId]: initialPrice } : { ...sel.priceByProductId };
            if (!willBeSelected) delete nextP[productId];
            productIdsToClear.forEach((id) => delete nextP[id]);
            state.suggested.selection.priceByProductId = nextP;

            // Limpiar errores del producto deseleccionado y de los limpiados
            const nextE = { ...sel.inputErrorByProductId };
            if (!willBeSelected) delete nextE[productId];
            productIdsToClear.forEach((id) => delete nextE[id]);
            state.suggested.selection.inputErrorByProductId = nextE;

            // Limpiar zona Elite del producto deseleccionado y de los limpiados
            const nextZ = { ...sel.eliteZoneByProductId };
            if (!willBeSelected) delete nextZ[productId];
            productIdsToClear.forEach((id) => delete nextZ[id]);
            state.suggested.selection.eliteZoneByProductId = nextZ;
        },
        setInputError: (
            state,
            action: PayloadAction<{ productId: number; field: 'price' | 'quantity' | 'elite_zone'; hasError: boolean }>
        ) => {
            const { productId, field, hasError } = action.payload;
            const current = state.suggested.selection.inputErrorByProductId[productId] ?? { price: false, quantity: false };
            state.suggested.selection.inputErrorByProductId[productId] = { ...current, [field]: hasError };
        },
        setEliteZone: (state, action: PayloadAction<{ productId: number; zone: string }>) => {
            const { productId, zone } = action.payload;
            state.suggested.selection.eliteZoneByProductId[productId] = zone;
        },
        setSelectionQuantity: (state, action: PayloadAction<{ productId: number; value: number }>) => {
            const { productId, value } = action.payload;
            state.suggested.selection.quantityByProductId[productId] = value;
        },
        setSelectionPrice: (state, action: PayloadAction<{ productId: number; value: number }>) => {
            const { productId, value } = action.payload;
            state.suggested.selection.priceByProductId[productId] = value;
        },
        setSelectedMonths: (state, action: PayloadAction<number | null>) => {
            state.suggested.selection.selectedMonths = action.payload;
            const months = action.payload;
            if (isPremiumMonthSelectionInvalid(months)) {
                const premiumProductIds = (state.suggested.products.category.premium ?? []).map((p) => p.product_id);
                const sel = state.suggested.selection;
                premiumProductIds.forEach((id) => {
                    delete sel.selectedByProductId[id];
                    delete sel.quantityByProductId[id];
                    delete sel.priceByProductId[id];
                    delete sel.inputErrorByProductId[id];
                });
            }
        },
        setSelectedPaymentMethod: (state, action: PayloadAction<number | null>) => {
            state.suggested.selection.selectedPaymentMethod = action.payload;
            state.suggested.selection.msiEnabled = false;
            state.suggested.selection.msiMonths = null;
        },
        setMsiEnabled: (state, action: PayloadAction<boolean>) => {
            state.suggested.selection.msiEnabled = action.payload;
            if (!action.payload) {
                state.suggested.selection.msiMonths = null;
            }
        },
        setMsiMonths: (state, action: PayloadAction<number | null>) => {
            state.suggested.selection.msiMonths = action.payload;
        },
        updateUserProfile: (
            state,
            action: PayloadAction<{ phone_contact: string; phone_whatsapp: string }>
        ) => {
            const { phone_contact, phone_whatsapp } = action.payload;
            state.suggested.user_profile.phone_contact = phone_contact;
            state.suggested.user_profile.phone_whatsapp = phone_whatsapp;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getSuggestedCalc.pending,(state, action) => {
                state.suggested.users.user_id = 0;
                state.loading = true;
                state.error = false;
            }
        );
        builder.addCase(getSuggestedCalc.fulfilled,(state, action) => {
                state.loading = false;
                if(!action.payload.success){
                    state.error = true;
                    state.errorMessage = action.payload.message;
                    state.suggested = initialShoppingCart.suggested;
                }else{
                    console.log(action.payload);
                    state.errorMessage = '';
                    state.error = false;
                    state.suggested = {
                        ...action.payload,
                        active_products_by_expiration_month:
                            action.payload.active_products_by_expiration_month ?? {},
                        selection: initialCommercialCartSelection,
                    };
                }
            }
        );
        builder.addCase(getSuggestedCalc.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export const {
    setSuggestions,
    toggleProductSelection,
    setSelectionQuantity,
    setSelectionPrice,
    setSelectedMonths,
    setSelectedPaymentMethod,
    setInputError,
    setEliteZone,
    updateUserProfile,
    setMsiEnabled,
    setMsiMonths,
} = shoppingCartSlice.actions;

export default shoppingCartSlice.reducer;
